import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyApprovalRow } from '../../api/types';
import { mapPaymentError } from '../../api/endpoints/payments';
import { Button } from '../../components/ui/Button';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { DataTable, type Column } from '../../components/ui/DataTable';
import { StatusIcon } from '../../components/ui/StatusIcon';
import { StatusPill } from '../../components/ui/StatusPill';
import { toast } from '../../components/ui/Toast';
import { formatMoney, parsePaise } from './allocation';
import { useApprovePayment, useMyApprovals, useRejectApproval } from './hooks';
import { OrderLabel } from './OrderLabel';
import { formatIstDayMon } from './paymentDates';
import { ReasonDialog } from './ReasonDialog';
import styles from './Payments.module.css';
import tabStyles from './MyApprovalsTab.module.css';

const money = (value: string) => formatMoney(parsePaise(value));

function WaitingPill({ name }: { name: string | null }) {
  return (
    <StatusPill
      bg="var(--status-amber-bg)"
      text="var(--status-amber-text)"
      label={name ? `Waiting for ${name}` : 'Waiting for approval'}
      icon={<StatusIcon name="hourglass" />}
    />
  );
}

/**
 * Payout requests the signed-in user made or may approve (#297, demo only).
 * Every amount is the server's; the maker of a request never sees Approve on it.
 */
export function MyApprovalsTab() {
  const { t } = useTranslation();
  const query = useMyApprovals();
  // This tab shows its own toast with the amount and order (no second, generic one).
  const approve = useApprovePayment({ successToast: false });
  const reject = useRejectApproval();
  const [approving, setApproving] = useState<MyApprovalRow | null>(null);
  const [rejecting, setRejecting] = useState<MyApprovalRow | null>(null);
  const rows = query.data ?? [];

  const columns: Column<MyApprovalRow>[] = [
    {
      key: 'order',
      header: 'Order',
      render: (r) => (
        <>
          <OrderLabel shipmentId={r.orderNo} />
          <div className={styles.subtle}>{r.partnerName}</div>
        </>
      ),
    },
    {
      key: 'amount',
      header: 'Advance',
      render: (r) => (
        <>
          <span className={styles.mono}>{money(r.amount)}</span>
          <div className={styles.subtle}>
            {r.cap != null && <>{t('payments.myApprovalsTab.laneLimit', { amount: money(r.cap) })}</>}
            {t('payments.myApprovalsTab.paidOnOrder', { amount: money(r.paidOnOrder) })}
          </div>
        </>
      ),
    },
    {
      key: 'reason',
      header: 'Why',
      width: '30%',
      render: (r) => (
        <>
          {r.reason ? <div>{r.reason}</div> : <div className={styles.subtle}>{t('payments.myApprovalsTab.withinLimit')}</div>}
          <div className={styles.subtle}>{t('payments.myApprovalsTab.askedBy', { name: r.makerName })}</div>
          {r.laneHistory.length > 0 && (
            <details className={tabStyles.history}>
              <summary>{t('payments.myApprovalsTab.laneHistory')}</summary>
              <ul>
                {r.laneHistory.map((h) => (
                  <li key={`${h.orderNo}-${h.date}`}>
                    #{h.orderNo} · {money(h.amount)} · {formatIstDayMon(h.date)}
                  </li>
                ))}
              </ul>
            </details>
          )}
        </>
      ),
    },
    {
      key: 'action',
      header: 'Status',
      width: '27%',
      render: (r) =>
        r.canApprove ? (
          <div className={tabStyles.rowActions}>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                approve.reset();
                setApproving(r);
              }}
            >
              {t('payments.myApprovalsTab.approve')}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                reject.reset();
                setRejecting(r);
              }}
            >
              {t('payments.myApprovalsTab.reject')}
            </Button>
          </div>
        ) : (
          <WaitingPill name={r.approverName} />
        ),
    },
  ];

  const confirmApprove = () => {
    if (!approving) return;
    const row = approving;
    approve.mutate(row.paymentId, {
      onSuccess: () => {
        setApproving(null);
        toast.success(`Approved ${money(row.amount)} for #${row.orderNo}.`);
      },
    });
  };

  return (
    <div>
      <div className={styles.sectionTitle}>
        <div>
          <h3>{t('payments.myApprovalsTab.title')}</h3>
          <p>{t('payments.myApprovalsTab.subtitle')}</p>
        </div>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        keyFn={(r) => r.paymentId}
        loading={query.isLoading}
        error={query.isError ? 'Couldn’t load your approvals' : undefined}
        onRetry={() => query.refetch()}
        empty="Nothing waiting for you. New advance requests show here."
      />
      <ConfirmDialog
        isOpen={approving != null}
        title={approving ? `Approve ${money(approving.amount)} for #${approving.orderNo}` : ''}
        description={
          approving
            ? `${approving.partnerName} gets this advance once someone marks it paid.`
            : ''
        }
        confirmText="Approve"
        variant="primary"
        loading={approve.isPending}
        errorMessage={approve.isError ? mapPaymentError(approve.error) : null}
        onConfirm={confirmApprove}
        onCancel={() => setApproving(null)}
      />
      {rejecting && (
        <ReasonDialog
          title={`Reject ${money(rejecting.amount)} for #${rejecting.orderNo}`}
          actionLabel="Reject request"
          help="reject-payout"
          context={<p>{t('payments.myApprovalsTab.rejectContext', { name: rejecting.makerName })}</p>}
          submitting={reject.isPending}
          error={reject.isError ? mapPaymentError(reject.error) : null}
          onCancel={() => setRejecting(null)}
          onSubmit={(note) => {
            const row = rejecting;
            reject.mutate(
              { paymentId: row.paymentId, note },
              {
                onSuccess: () => {
                  setRejecting(null);
                  toast.success(`Rejected #${row.orderNo}. ${row.makerName} sees your reason.`);
                },
              },
            );
          }}
        />
      )}
    </div>
  );
}
